/**
 * @file Stages an inbound email's importable attachments for an agent import run.
 * Reads the `emailAnalyses` document, downloads each uploaded attachment blob
 * from Storage, and returns them as `File`s so the notification card can hand
 * them to `AIContext.startDocumentImportRun` — the same path a dropped file takes.
 * This replaces the legacy `ExtractReviewOverlay` review of the pre-staged
 * `extractions/` doc: the agent now re-imports the attachments live.
 */
import { doc, getDoc } from 'firebase/firestore';
import { getBytes, ref } from 'firebase/storage';
import type { EmailAnalysis } from '#types';
import { firestore } from './startFirestore';
import { storage } from './startStorage';

export async function loadEmailAttachmentFiles(analysisId: string): Promise<File[]> {
  const snapshot = await getDoc(doc(firestore, 'emailAnalyses', analysisId));
  const analysis = snapshot.data() as EmailAnalysis | undefined;
  const references = (analysis?.attachmentReferences ?? []).filter((a) => a.storagePath);
  return Promise.all(
    references.map(async (a) => {
      const bytes = await getBytes(ref(storage, a.storagePath as string));
      return new File([bytes], a.filename ?? 'attachment', { type: a.contentType });
    }),
  );
}
